import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Loader, Text } from "@mantine/core";
import { Alert, Card, Button } from "@xplatform/design-system";
import { IconArrowLeft, IconDownload } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useMonitoring } from "@hooks/useMonitoring";
import {
    ProcessInfoSection,
    ProcessItemsTable,
    CostBreakdownSection,
} from "@features/Monitoring";
import type { IProcessDetailViewModel } from "@models/extraction";
import type { ExternalRefCapabilityUsageViewModel } from "@models/monitoring";
import { intelligenceEngineApi } from "@services/extractionApi";

import "./ProcessDetailPage.scss";

export const ProcessDetailPage = () => {
    const { processId } = useParams<{ processId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { createdBy, organisationName, doctypeName } = (location.state as { createdBy?: string; organisationName?: string; doctypeName?: string }) || {};
    const { fetchProcessDetail, fetchProcessCapabilityUsage, isLoading, error } =
        useMonitoring();
    const [processDetail, setProcessDetail] =
        useState<IProcessDetailViewModel | null>(null);
    const [capabilityUsage, setCapabilityUsage] = useState<ExternalRefCapabilityUsageViewModel[]>([]);
    const [isDownloadingAll, setIsDownloadingAll] = useState(false);

    useEffect(() => {
        if (processId) {
            fetchProcessDetail(processId).then((detail) => {
                if (detail) {
                    setProcessDetail(detail);
                }
            });
            fetchProcessCapabilityUsage("extraction_process", processId).then((usage) => {
                if (usage) {
                    setCapabilityUsage(usage);
                }
            });
        }
    }, [processId, fetchProcessDetail, fetchProcessCapabilityUsage]);

    const handleDownloadAllInputs = async () => {
        if (!processId) return;
        setIsDownloadingAll(true);
        try {
            const result = await intelligenceEngineApi.downloadAllInputFiles(processId);
            window.open(result.s3_key, "_blank");
        } catch (err) {
            console.error("Error downloading all input files:", err);
        } finally {
            setIsDownloadingAll(false);
        }
    };

    if (isLoading) {
        return (
            <div className="process-detail-page">
                <div className="process-detail-page__loading">
                    <Loader />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="process-detail-page">
                <Alert color="red" mb="sm">
                    {error}
                </Alert>
                <Button
                    variant="subtle"
                    leftSection={<IconArrowLeft size={16} />}
                    onClick={() => navigate("/admin/monitoring")}
                    size="sm"
                >
                    Back to Monitoring
                </Button>
            </div>
        );
    }

    if (!processDetail) {
        return (
            <div className="process-detail-page">
                <Alert color="blue" mb="sm">
                    Process not found
                </Alert>
                <Button
                    variant="subtle"
                    leftSection={<IconArrowLeft size={16} />}
                    onClick={() => navigate("/admin/monitoring")}
                    size="sm"
                >
                    Back to Monitoring
                </Button>
            </div>
        );
    }

    return (
        <div className="process-detail-page">
            <div className="process-detail-page__header">
                <Button
                    variant="subtle"
                    leftSection={<IconArrowLeft size={16} />}
                    onClick={() => navigate("/admin/monitoring")}
                    size="sm"
                >
                    Back
                </Button>
                <Text
                    size="lg"
                    fw={600}
                    component="h1"
                    className="process-detail-page__title"
                >
                    Process Details
                </Text>
            </div>

            {processDetail.errorMessage && (
                <Alert color="red" mb="sm">
                    {processDetail.errorMessage}
                </Alert>
            )}

            <Card withBorder>
                <Text size="md" fw={600} mb="sm">
                    {t("admin.monitoring.costBreakdown.processInfo")}
                </Text>
                <ProcessInfoSection
                    processDetail={processDetail}
                    createdBy={createdBy}
                    organisationName={organisationName}
                    doctypeName={doctypeName} />
            </Card>

            <Card withBorder>
                <Text size="md" fw={600} mb="sm">
                    {t("admin.monitoring.costBreakdown.title")}
                </Text>
                <CostBreakdownSection capabilityUsage={capabilityUsage} />
            </Card>

            <ProcessItemsTable
                items={processDetail.processItems}
                processId={processDetail.id}
                downloadAllButton={
                    <Button
                        variant="light"
                        size="xs"
                        leftSection={<IconDownload size={14} />}
                        loading={isDownloadingAll}
                        onClick={handleDownloadAllInputs}
                    >
                        {t("admin.monitoring.download.downloadAllInputs")}
                    </Button>
                }
            />
        </div>
    );
};
